'use client';

import { Dialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { cx } from './cx';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Подзаголовок под названием. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Кнопки внизу окна — встают капсулами СТОЛБИКОМ: последняя (действие) сверху,
   *  первая («Отмена») снизу. Разрушительное действие — `tone="danger"`. */
  footer?: ReactNode;
  /** Ширина окна на широком экране; по умолчанию роль `--dlg-w` (320), в приложении 460. */
  width?: number;
  className?: string;
};

/**
 * Окно-вопрос поверх страницы («Точно удалить?», «Выйти из аккаунта?»).
 *
 * ВИД ПО РОДНОМУ СПИСКУ iOS (ТЗ WEB-LOOK Э4.5, 30.09.2026; `WizAskSheet`): на широком экране —
 * узкое окно по центру, заголовок и текст по центру, действия капсулами столбиком; на
 * телефоне в браузере то же содержимое выезжает НИЖНИМ ЛИСТОМ (грабер, радиус сверху,
 * капсулы 52) — это CSS того же окна (`kit.css`, «Окно поверх страницы»), слой один.
 *
 * INVARIANT(scriptera): в iOS-приложении этим окном не пользуются — там слои делает
 * шторочный каркас (и в приложении окно держит прежний вид, блок `:where(html.app)`).
 * Причина: у шторок свои железные правила (стопка отдельных слоёв, единое направление с
 * мобильным сайтом, нативная шапка, safe-area), и своё окно поверх них ломает и жесты, и
 * возврат назад. Канон: docs/NATIVE-SLIDE-PLAN.md §11. Нужен ШАГ (форма, выбор) внутри
 * шторки — это уровень шторки, а не этот компонент; окно — только короткий вопрос.
 */
export function KDialog({ open, onOpenChange, title, subtitle, children, footer, width, className }: Props) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="k k-ovl" />
        <Dialog.Content
          className={cx('k k-dlg bub-layer', className)}
          style={width ? { ['--k-dlg-w' as string]: `${width}px` } : undefined}
        >
          <div className="k-dlg__grab" aria-hidden="true" />
          <div className="k-dlg__head">
            <Dialog.Title className="k-dlg__title">{title}</Dialog.Title>
            {subtitle ? <Dialog.Description className="k-dlg__sub">{subtitle}</Dialog.Description> : null}
          </div>
          <div className="k-dlg__body">{children}</div>
          {footer ? <div className="k-dlg__foot">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
